import React, { useState, useEffect, useCallback } from "react";
import { logout, getCategories } from "../api";
import socket from "../socket";
import PersonalList from "../components/PersonalList";
import SharedList from "../components/SharedList";
import AdminPanel from "../components/AdminPanel";
import AddItemForm from "../components/AddItemForm";
import Toast from "../components/Toast";

/**
 * Dashboard principal — après connexion.
 * Onglets : "Ma liste", "Liste commune", "Admin" (si admin).
 */
function Dashboard({ user, onLogout }) {
  const [activeTab, setActiveTab] = useState("personal");
  const [showAddForm, setShowAddForm] = useState(false);
  const [categories, setCategories] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    getCategories().then(setCategories).catch(console.error);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      onLogout();
    } catch (err) {
      console.error("Erreur lors de la déconnexion:", err);
    }
  };

  const refresh = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  // WebSocket pour les mises à jour temps réel
  useEffect(() => {
    socket.connect();
    const handleUpdate = () => refresh();
    socket.on("list_updated", handleUpdate);
    return () => {
      socket.off("list_updated", handleUpdate);
      socket.disconnect();
    };
  }, [refresh]);

  const handleItemAdded = () => {
    setShowAddForm(false);
    refresh();
    setToast({ message: "Article ajouté !", type: "success" });
  };

  return (
    <div className="dashboard">
      {/* En-tête */}
      <header className="dashboard-header">
        <h1 className="dashboard-title">
          <span role="img" aria-label="chantier">&#x1F3D7;</span> Sobeca
        </h1>
        <div className="dashboard-user">
          <span className="user-badge" style={{ backgroundColor: user?.color }}>
            {user?.emoji} {user?.username}
          </span>
          <button className="logout-btn" onClick={handleLogout} aria-label="Se déconnecter" title="Se déconnecter">
            &#x1F6AA;
          </button>
        </div>
      </header>

      {/* Onglets */}
      <div className="tabs" role="tablist">
        <button
          className={`tab ${activeTab === "personal" ? "active" : ""}`}
          onClick={() => setActiveTab("personal")}
          role="tab"
          aria-selected={activeTab === "personal"}
        >
          Ma liste
        </button>
        <button
          className={`tab ${activeTab === "shared" ? "active" : ""}`}
          onClick={() => setActiveTab("shared")}
          role="tab"
          aria-selected={activeTab === "shared"}
        >
          Liste commune
        </button>
        {user?.is_admin && (
          <button
            className={`tab ${activeTab === "admin" ? "active" : ""}`}
            onClick={() => setActiveTab("admin")}
            role="tab"
            aria-selected={activeTab === "admin"}
          >
            Admin
          </button>
        )}
      </div>

      {/* Contenu de l'onglet actif */}
      <div className="tab-content" role="tabpanel" id="main-content">
        {activeTab === "personal" && (
          <PersonalList key={`personal-${refreshKey}`} onRefresh={refresh} categories={categories} />
        )}
        {activeTab === "shared" && (
          <SharedList key={`shared-${refreshKey}`} onRefresh={refresh} isAdmin={user?.is_admin} />
        )}
        {activeTab === "admin" && user?.is_admin && (
          <AdminPanel />
        )}
      </div>

      {/* Bouton flottant d'ajout (sauf onglet admin) */}
      {activeTab !== "admin" && (
        <button
          className="fab"
          onClick={() => { if (!showAddForm) setShowAddForm(true); }}
          aria-label="Ajouter un article"
          title="Ajouter un article"
        >
          +
        </button>
      )}

      {/* Modal d'ajout d'article */}
      {showAddForm && (
        <AddItemForm
          categories={categories}
          onAdd={handleItemAdded}
          onClose={() => setShowAddForm(false)}
        />
      )}

      {/* Toast global */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onExpire={() => setToast(null)}
        />
      )}
    </div>
  );
}

export default Dashboard;
